import { useMemo } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { ChartCard } from '@/shared/components/dashboard/ChartCard';
import { KpiCard, KpiGrid } from '@/shared/components/dashboard/KpiCard';
import { PageHero } from '@/shared/components/dashboard/PageHero';
import { barOption, donutOption } from '@/shared/components/dashboard/chartOptions';
import { ErrorPanel, LoadingPanel } from '@/shared/components/dashboard/panels';
import { DataTable } from '@/shared/components/ui/DataTable';
import type { ColumnDef, FilterDef } from '@/shared/components/ui/DataTable';
import { Icons } from '@/shared/components/ui/icons';
import { Button } from '@/shared/components/ui/button';
import { cn } from '@/shared/lib/cn';
import { usePlans } from '../hooks/usePlans';
import { useDeletePlan } from '../hooks/useDeletePlan';
import type { PlanResponseDTO } from '../types';

const C = {
  teal: '#14b8a6',
  blue: '#3b82f6',
  emerald: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  violet: '#8b5cf6',
  cyan: '#06b6d4',
  orange: '#f97316',
};

function PeriodBadge({ period }: { period: string }) {
  const { t } = useTranslation();
  const colors: Record<string, string> = {
    monthly: 'bg-blue-100 text-blue-700',
    quarterly: 'bg-violet-100 text-violet-700',
    yearly: 'bg-emerald-100 text-emerald-700',
  };
  const labelKey = `plans.periods.${period}` as const;
  const label = ['monthly', 'quarterly', 'yearly'].includes(period) ? t(labelKey) : period;
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium',
        colors[period] ?? 'bg-muted text-muted-foreground',
      )}
    >
      {label}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium',
        active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-600',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', active ? 'bg-emerald-500' : 'bg-gray-400')} />
      {active ? t('products.status.active') : t('products.status.inactive')}
    </span>
  );
}

export function PlansListPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading, error } = usePlans({});
  const deletePlan = useDeletePlan();

  const canCreate = user?.permissions.includes('plans.create');
  const plans = useMemo(() => data?.items ?? [], [data?.items]);
  const activeCount = plans.filter((p) => p.is_active).length;
  const monthlyCount = plans.filter((p) => p.billing_period === 'monthly').length;
  const yearlyCount = plans.filter((p) => p.billing_period === 'yearly').length;
  const quarterlyCount = plans.filter((p) => p.billing_period === 'quarterly').length;
  const avgPrice =
    plans.length === 0
      ? 0
      : Math.round(plans.reduce((s, p) => s + p.price_amount, 0) / plans.length);

  const periodOption = useMemo(
    () =>
      donutOption(t('plans.billingPeriod'), [
        { name: t('plans.periods.monthly'), value: monthlyCount },
        { name: t('plans.periods.quarterly'), value: quarterlyCount },
        { name: t('plans.periods.yearly'), value: yearlyCount },
      ]),
    [monthlyCount, quarterlyCount, t, yearlyCount],
  );

  const priceOption = useMemo(
    () =>
      barOption(
        t('plans.price'),
        plans.slice(0, 8).map((p) => p.slug),
        plans.slice(0, 8).map((p) => p.price_amount),
        true,
      ),
    [plans, t],
  );

  const gaugeOption = useMemo(() => {
    const pct = plans.length === 0 ? 0 : Math.round((activeCount / plans.length) * 100);
    return {
      series: [
        {
          type: 'gauge',
          center: ['50%', '60%'],
          radius: '88%',
          startAngle: 200,
          endAngle: -20,
          min: 0,
          max: 100,
          splitNumber: 5,
          axisLine: {
            lineStyle: {
              width: 12,
              color: [
                [0.3, C.rose],
                [0.7, C.amber],
                [1, C.emerald],
              ],
            },
          },
          pointer: { itemStyle: { color: 'auto' }, length: '65%', width: 6 },
          axisTick: { distance: -18, length: 6, lineStyle: { color: '#fff', width: 2 } },
          splitLine: { distance: -24, length: 14, lineStyle: { color: '#fff', width: 3 } },
          axisLabel: { color: 'inherit', distance: 20, fontSize: 11 },
          detail: {
            valueAnimation: true,
            formatter: '{value}%',
            color: 'inherit',
            fontSize: 22,
            fontWeight: 700,
            offsetCenter: [0, '30%'],
          },
          title: { offsetCenter: [0, '55%'], fontSize: 11, color: '#9ca3af' },
          data: [{ value: pct, name: t('plans.charts.activePlans') }],
        },
      ],
    };
  }, [activeCount, plans.length, t]);

  const waterfallOption = useMemo(() => {
    const sorted = [...plans].sort((a, b) => a.price_amount - b.price_amount).slice(0, 6);
    const base = sorted[0]?.price_amount ?? 0;
    const ax = {
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisLabel: { color: '#9ca3af', fontSize: 10 },
      splitLine: { lineStyle: { color: '#f3f4f6' } },
    };
    return {
      tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
      grid: { left: 52, right: 16, top: 16, bottom: 48 },
      xAxis: { type: 'category', data: sorted.map((p) => p.slug), ...ax },
      yAxis: { type: 'value', ...ax },
      series: [
        {
          type: 'bar',
          stack: 'total',
          silent: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent' },
          data: sorted.map((p, i) =>
            i === 0 ? 0 : sorted.slice(0, i).reduce((s, pp) => s + (pp.price_amount - base), base),
          ),
        },
        {
          type: 'bar',
          name: t('plans.charts.priceSeries', { currency: sorted[0]?.currency ?? 'SAR' }),
          stack: 'total',
          barMaxWidth: 56,
          data: sorted.map((p, i) => ({
            value:
              i === 0 ? p.price_amount : p.price_amount - (sorted[i - 1]?.price_amount ?? base),
            itemStyle: { color: C.teal, borderRadius: [6, 6, 0, 0] },
          })),
        },
      ],
    };
  }, [plans, t]);

  const columns: ColumnDef<PlanResponseDTO>[] = useMemo(
    () => [
      {
        key: 'name',
        header: t('plans.title'),
        accessor: (p) => (
          <button
            type="button"
            className="flex items-center gap-3 text-start"
            onClick={() => void navigate({ to: `/dashboard/plans/${p.id}/detail` as never })}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icons.Layers className="h-4 w-4" />
            </span>
            <div className="min-w-0">
              <p className="font-medium text-primary hover:underline leading-tight">{p.name_en}</p>
              <p className="text-xs text-muted-foreground" dir="rtl">
                {p.name_ar}
              </p>
            </div>
          </button>
        ),
        sortValue: (p) => [p.name_en, p.name_ar ?? '', p.slug].join(' '),
      },
      {
        key: 'slug',
        header: t('products.slug'),
        accessor: (p) => <span className="font-mono text-xs text-muted-foreground">{p.slug}</span>,
        sortValue: (p) => p.slug,
        align: 'center',
      },
      {
        key: 'billing_period',
        header: t('plans.billingPeriod'),
        accessor: (p) => <PeriodBadge period={p.billing_period} />,
        sortValue: (p) => p.billing_period,
        align: 'center',
      },
      {
        key: 'price',
        header: t('plans.price'),
        accessor: (p) => (
          <span className="font-semibold text-foreground tabular-nums">
            {p.price_amount.toLocaleString()}{' '}
            <span className="text-xs font-normal text-muted-foreground">{p.currency}</span>
          </span>
        ),
        sortValue: (p) => p.price_amount,
        align: 'center',
      },
      {
        key: 'trial_days',
        header: t('plans.trialDays'),
        accessor: (p) =>
          p.trial_days > 0 ? (
            <span className="inline-flex items-center gap-1 text-xs text-primary">
              <Icons.Timer className="h-3 w-3" />
              {p.trial_days}d
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
        sortValue: (p) => p.trial_days,
        align: 'center',
      },
      {
        key: 'status',
        header: t('common.status'),
        accessor: (p) => <ActiveBadge active={p.is_active} />,
        sortValue: (p) => (p.is_active ? 'active' : 'inactive'),
        align: 'center',
      },
      {
        key: 'actions',
        header: '',
        accessor: (p) => (
          <div className="flex items-center justify-end gap-1">
            <button
              title={t('common.view')}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-primary transition-colors"
              onClick={() => void navigate({ to: `/dashboard/plans/${p.id}/detail` as never })}
            >
              <Icons.Eye className="h-3.5 w-3.5" />
            </button>
            <button
              title={t('products.edit')}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
              onClick={() => void navigate({ to: `/dashboard/plans/${p.id}` as never })}
            >
              <Icons.Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              title={t('products.actions.delete')}
              className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
              disabled={deletePlan.isPending}
              onClick={() => {
                if (confirm(t('plans.deleteConfirm'))) deletePlan.mutate(p.id);
              }}
            >
              <Icons.Trash className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
        align: 'right',
      },
    ],
    [t, navigate, deletePlan],
  );

  const filters = useMemo<FilterDef[]>(
    () => [
      {
        key: 'name',
        label: t('common.search'),
        type: 'text',
        placeholder: t('plans.searchPlaceholder'),
      },
      {
        key: 'billing_period',
        label: t('plans.billingPeriod'),
        type: 'select',
        options: [
          { label: t('plans.periods.monthly'), value: 'monthly' },
          { label: t('plans.periods.quarterly'), value: 'quarterly' },
          { label: t('plans.periods.yearly'), value: 'yearly' },
        ],
      },
      {
        key: 'status',
        label: t('common.status'),
        type: 'select',
        options: [
          { label: t('products.status.active'), value: 'active' },
          { label: t('products.status.inactive'), value: 'inactive' },
        ],
      },
    ],
    [t],
  );

  if (isLoading) return <LoadingPanel />;
  if (error) return <ErrorPanel message={t('common.error')} />;

  return (
    <div className="w-full min-w-0 space-y-6 animate-fade-in">
      <PageHero
        title={t('plans.title')}
        subtitle={t('dashboard.modules.plans')}
        icon={<Icons.Layers className="h-7 w-7" />}
        eyebrow={t('plans.dashboard.pricingPlans')}
        metrics={[
          { label: t('plans.title'), value: String(data?.total ?? plans.length) },
          { label: t('plans.isActive'), value: String(activeCount) },
          {
            label: t('plans.price'),
            value: `${avgPrice.toLocaleString()} ${plans[0]?.currency ?? 'SAR'}`,
          },
        ]}
        actions={
          canCreate ? (
            <Button onClick={() => void navigate({ to: '/dashboard/plans/new' })}>
              <Icons.Plus className="me-1.5 h-4 w-4" />
              {t('plans.create')}
            </Button>
          ) : null
        }
      />

      <KpiGrid>
        <KpiCard
          title={t('plans.title')}
          value={String(data?.total ?? plans.length)}
          icon={<Icons.Layers className="h-4 w-4" />}
          tone="blue"
          hint={t('plans.metrics.allPlans')}
        />
        <KpiCard
          title={t('plans.isActive')}
          value={String(activeCount)}
          icon={<Icons.CheckCircle className="h-4 w-4" />}
          tone="green"
        />
        <KpiCard
          title={t('plans.price')}
          value={avgPrice.toLocaleString()}
          icon={<Icons.DollarSign className="h-4 w-4" />}
          tone="amber"
          hint={plans[0]?.currency ?? 'SAR'}
        />
        <KpiCard
          title={t('plans.trialDays')}
          value={String(plans.reduce((max, p) => Math.max(max, p.trial_days), 0))}
          icon={<Icons.Timer className="h-4 w-4" />}
          tone="violet"
          hint={t('plans.metrics.maxTrialDays')}
        />
      </KpiGrid>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <ChartCard
          title={t('plans.billingPeriod')}
          subtitle={t('plans.charts.distributionByPeriod')}
          option={periodOption}
        />
        <ChartCard
          title={t('plans.price')}
          subtitle={t('plans.charts.priceByPlan')}
          option={priceOption}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:gap-6 lg:grid-cols-2">
        <ChartCard
          title={t('plans.charts.activePlanCoverage')}
          subtitle={t('plans.charts.activePlanCoverageSubtitle')}
          option={gaugeOption}
          height={260}
        />
        <ChartCard
          title={t('plans.charts.priceComparison')}
          subtitle={t('plans.charts.priceComparisonSubtitle')}
          option={waterfallOption}
          height={260}
        />
      </div>

      <DataTable
        data={plans}
        columns={columns}
        filters={filters}
        rowKey={(p) => p.id}
        selectable
        total={data?.total ?? plans.length}
        initialPageSize={10}
        emptyMessage={t('plans.notFound')}
      />
    </div>
  );
}
